/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {map, Observable, throwError} from 'rxjs';

import {ApiConfig} from '../config/api-config';
import {AdnLogService} from '../../components/util/adn-log.service';
import {IpAddress} from '../../components/util/ip-valid';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';

@Injectable({providedIn: 'root'})
export class AdUnitDiagnosticsService {
  constructor(
    private readonly client: AdnApiClientService,
    private readonly adnLog: AdnLogService,
    private readonly apiConfig: ApiConfig
  ) {
  }

  run(params: any): Observable<any> {
    if (!params.auId) {
      this.adnLog.warn('Need an auId to do the diagnostic request.');
    }
    const query = _.clone(params);
    query.segments = this.updateParams(query.segments);
    query.c = this.updateParams(query.c);
    query.keywords = this.updateParams(query.keywords);

    if (_.isArray(query.kv)) {
      query.kv = _.map(_.filter(query.kv, (kv) => _.trim(kv.k) && _.trim(kv.v)), (kv) => {
        return {[_.trim(kv.k)]: this.updateParams(kv.v)};
      });
    }
    if (query.requestType === 'NATIVE') {
      query.tt = 'native';
    } else if (query.requestType === 'VAST') {
      query.tt = 'vast';
    }

    const validationError = this.validateLocation(query);
    if (validationError) {
      return throwError(() => ({info: validationError}));
    }

    return this.client.request<any>({
      method: 'GET',
      url: this.apiConfig.getVersionedUri(['adunits', 'diagnostic']),
      params: {format: 'dx', tzo: new Date().getTimezoneOffset(), gdpr: 0, ...query},
      responseType: 'text'
    }).pipe(map((data) => this.parseResponse(data)));
  }

  private validateLocation(query: any): string | null {
    if (query.ip && !IpAddress.isValid(query.ip)) {
      return 'Must enter a valid IP address to test against';
    }
    if (query.longitude || query.latitude) {
      if (!(query.longitude && query.latitude)) {
        return 'Both latitude and longitude must be specified. Not just one or the other.';
      }
      if (_.get(query, 'longitude').length > 0 && isNaN(query.longitude)) {
        return 'If specified, both latitude and longitude must be a number.';
      }
      if (_.get(query, 'latitude').length > 0 && isNaN(query.latitude)) {
        return 'If specified, both latitude and longitude must be a number.';
      }
      query.latitude = parseFloat(query.latitude);
      query.longitude = parseFloat(query.longitude);
    }
    return null;
  }

  private updateParams(value: any): any {
    if (!value) {
      return value;
    }
    return _.map(_.filter(value.split(','), (part: string) => _.trim(part)), (part: string) => _.trim(part));
  }

  private parseResponse(data: any): any {
    if (!_.isString(data)) {
      return data;
    }
    const sanitised = data.split('').filter((character: string) => {
      const charCode = character.charCodeAt(0);
      return !(charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13);
    }).join('');
    return JSON.parse(sanitised);
  }
}
